// [전체 코드 미사용/주석 삭제 금지]

// Base imports
import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "config/apiConfig";

// App.css만 남기고, 개별 CSS 파일 임포트 제거
import 'styles/Font.css';

// 이미지 임포트
import remoteNum from "assets/images/icon/input-delete-icon.svg";

// 컴포넌트 및 훅 임포트
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import useVerificationTimer from "hooks/useVerificationTimer";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";

// 휴대폰 본인인증 페이지
function Verification() {
    usePreventInputScroll();
    usePreventHorizontalScroll();
    const containerRef = useRef(null);
    const navigate = useNavigate();

    // 동적 높이 측정을 위한 ref 및 state
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    // 상태
    const [phoneNumber, setPhoneNumber] = useState('');
    const [authNumber, setAuthNumber] = useState('');
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isPhoneFocused, setIsPhoneFocused] = useState(false);
    // 인증번호 입력 필드 포커스 상태
    const [isAuthFocused, setIsAuthFocused] = useState(false);
    const [showSentMessage, setShowSentMessage] = useState(false);

    // API 통신을 위한 상태
    const [isLoading, setIsLoading] = useState(false);
    // 기존 errorMessage 상태 제거 -> alertModal 상태로 대체
    const [alertModal, setAlertModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null // 확인 버튼 클릭 시 실행할 함수 (선택적)
    });

    // 파생 상태
    const isRequestButtonActive = phoneNumber.length >= 10 && !isCodeSent;
    const isVerifyButtonActive = isCodeSent && authNumber.length === 6;

    // 헤더 높이 측정 로직
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        // 초기 로드 시 측정
        measureHeight();

        // 레이아웃 시프트 대비 (폰트 로딩 등)
        setTimeout(measureHeight, 100);

        // 리사이즈 이벤트 감지
        window.addEventListener('resize', measureHeight);

        return () => window.removeEventListener('resize', measureHeight);
    }, []);

    // 모달 닫기 핸들러
    const closeAlertModal = () => {
        setAlertModal(prev => ({ ...prev, isOpen: false }));
    };

    // sendsms API 호출 함수
    /**
     * /api/sendsms로 인증번호를 요청하는 함수
     * @param {boolean} isResend - 재전송 여부
     */
    const handleRequestCode = async (isResend = false) => {
        const API_URL = `${API_BASE_URL}/sendsms`;

        if (isLoading) return; // 중복 요청 방지

        setIsLoading(true);
        setShowSentMessage(false); // 기존 토스트 숨기기

        try {
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                // 휴대폰 번호를 body에 담아 전송
                body: new URLSearchParams({ phoneNumber: phoneNumber }),
            });

            // 서버가 2xx 응답이 아닌 경우, 응답 본문을 JSON으로 파싱
            if (!response.ok) {
                let errorData = {};
                try {
                    // 서버가 { "message": "에러내용" } 형태의 JSON을 보낸다고 가정
                    errorData = await response.json();
                } catch (e) {
                    // JSON 파싱 실패 시 (예: 500 에러)
                    throw new Error('인증번호 전송에 실패했습니다.');
                }
                // data.message가 있으면 사용, 없으면 기본 메시지
                throw new Error(errorData.message || '인증번호 전송에 실패했습니다.');
            }

            // --- API 호출 성공 ---

            if (isResend) {
                handleResendClick(); // 타이머 훅의 재시작 함수 호출
            } else {
                setIsCodeSent(true); // 인증번호 입력창으로 전환
            }
            setShowSentMessage(true); // "발송 완료" 토스트 표시

        } catch (err) {
            // 에러 토스트 -> 모달로 변경
            setAlertModal({
                isOpen: true,
                title: '전송 실패',
                message: err.message,
                onConfirm: () => navigate('/', { replace: true }) // 확인 시 로그인 페이지로 이동
            });
        } finally {
            setIsLoading(false); // 로딩 종료
        }
    };


    // 하단 버튼 클릭
    // --- ▼ verifysms API 연동 ---
    const handleBottomButtonClick = async () => {
        const API_URL = `${API_BASE_URL}/verifysms`;

        if (isCodeSent) {
            // "확인" (인증번호 일치 확인) 로직
            if (!isVerifyButtonActive || isLoading) return; // 로딩 중 체크

            setIsLoading(true);

            try {
                // 인증번호 확인 API 호출
                const response = await fetch(API_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams({
                        phoneNumber: phoneNumber,
                        authCode: authNumber // API 스펙에 맞게 'authCode'로 전송
                    })
                });

                const data = await response.json();
                const res = data.data;

                if (res.code !== 200) {
                    let errorData = {};
                    try {
                        errorData = await response.json();
                    } catch (e) {
                        throw new Error('인증번호 확인에 실패했습니다.');
                    }
                    // // 예: "인증번호가 일치하지 않습니다."
                    throw new Error('인증번호가 올바르지 않습니다.');
                }

                // --- 인증 성공 ---
                // 기존 로직 (세션 저장 및 페이지 이동)
                const dataFromStorage = sessionStorage.getItem('tempSignUpData');
                const existingData = dataFromStorage ? JSON.parse(dataFromStorage) : {};
                const updatedData = { ...existingData, phoneNumber: phoneNumber };
                sessionStorage.setItem('tempSignUpData', JSON.stringify(updatedData));

                navigate('/signup/branch');

            } catch (err) {
                console.error(err);
                // 에러 토스트 -> 모달로 변경
                setAlertModal({
                    isOpen: true,
                    title: '인증 실패',
                    message: '인증번호를 확인해 주세요.'
                })
            } finally {
                setIsLoading(false);
            }

        } else {
            // "인증번호 요청" 로직
            if (!isRequestButtonActive || isLoading) return; // 로딩 중 체크
            await handleRequestCode(false); // API 호출 함수 실행
        }
    };
    // --- ▲ ---

    // 입력 핸들러
    const handlePhoneNumberChange = (e) => {
        const numericValue = e.target.value.replace(/[^0-9]/g, '');
        setPhoneNumber(numericValue);
    };
    const handleAuthNumberChange = (e) => {
        const numericValue = e.target.value.replace(/[^0-9]/g, '');
        setAuthNumber(numericValue);
    };

    // '발송 완료' 메시지 3초 후 숨기기
    useEffect(() => {
        if (showSentMessage) {
            const timer = setTimeout(() => setShowSentMessage(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [showSentMessage]);

    // 커스텀 훅: 인증 타이머
    const { formattedTime, handleResendClick } = useVerificationTimer(isCodeSent);

    // 재전송
    const handleResend = async () => {
        if (isLoading) return; // 로딩 중 클릭 방지
        await handleRequestCode(true); // API 호출 함수 실행 (재전송 모드)
    };

    // 버튼 활성화 상태 (스타일 적용용)
    const isButtonActive = isRequestButtonActive || isVerifyButtonActive;
    const isPhoneInputActive = (isPhoneFocused || phoneNumber.length > 0) && !isCodeSent;
    const isAuthInputActive = (isAuthFocused || authNumber.length > 0); // isAuthFocused 조건 추가

    // 동적 스타일 적용 (paddingTop 계산)
    const dynamicMainStyle = {
        ...staticStyles.mainContent,
        paddingTop: `${headerHeight}px`,
    };

    return (
        // 최상위 레이아웃
        <div className="container verification-container" ref={containerRef} style={staticStyles.backgroundWrapper}>

            {/* [수정] ref 연결 */}
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div className="statusBar" style={staticStyles.statusBar} />
                {/* title 프롭 추가 */}
                <TopNavigator title="본인 인증" />
            </div>

            {/* [수정] 동적 스타일(dynamicMainStyle) 적용 */}
            <main className="content-wrapper" style={dynamicMainStyle}>

                {/* 페이지 제목 (marginTop: 25px 적용) */}
                <div className="verification-info-field" style={staticStyles.verificationInfoField}>
                    <p className="verification-info-title" style={staticStyles.verificationInfoTitle}>휴대폰 본인인증</p>
                    <p className="verification-info-subtitle" style={staticStyles.verificationInfoSubtitle}>
                        하입하우스 회원 여부 확인과 가입을 위해<br /> 휴대폰 본인인증이 필요해요
                    </p>
                </div>

                {/* 인증 폼 */}
                <div className="verification-form" style={staticStyles.verificationForm}>
                    <label className="telnum-label" style={staticStyles.telnumLabel}>휴대폰번호</label>
                    <div className="input-container" style={staticStyles.inputContainer}>
                        <input
                            // App.css의 .has-value 클래스 활용
                            className={`common-input ${isPhoneInputActive ? 'has-value' : ''}`}
                            // 포커스 상태에 따라 스타일 조건부 적용
                            style={{
                                ...staticStyles.commonInput,
                                ...(isPhoneInputActive && isPhoneFocused ? staticStyles.inputFocused : {})
                            }}
                            type="tel"
                            placeholder="휴대폰번호 입력"
                            value={phoneNumber}
                            onChange={handlePhoneNumberChange} disabled={isCodeSent} inputMode="numeric"
                            onFocus={() => setIsPhoneFocused(true)}
                            onBlur={() => setIsPhoneFocused(false)}
                        />
                        {phoneNumber.length > 0 && !isCodeSent && (
                            <img
                                src={remoteNum}
                                alt="입력 내용 지우기"
                                className="clear-button"
                                style={staticStyles.clearButton}
                                onClick={() => setPhoneNumber('')}
                            />
                        )}
                    </div>

                    {isCodeSent && (
                        <div className="input-container" style={staticStyles.inputContainer}>
                            <input
                                className={`common-input ${isAuthInputActive ? 'has-value' : ''}`}
                                // 포커스 상태에 따라 스타일 조건부 적용
                                style={{
                                    ...staticStyles.commonInput,
                                    ...(isAuthInputActive && isAuthFocused ? staticStyles.inputFocused : {})
                                }}
                                type="tel"
                                placeholder="인증번호 입력"
                                value={authNumber}
                                onWheel={event => event.target.blur()}
                                onChange={handleAuthNumberChange}
                                inputMode="numeric"
                                // 포커스 이벤트 핸들러
                                onFocus={() => setIsAuthFocused(true)}
                                onBlur={() => setIsAuthFocused(false)}
                            />
                            <span className="timer" style={staticStyles.timer}>
                                {formattedTime}
                            </span>
                        </div>
                    )}

                    {!isCodeSent &&
                        <p className="infoText" style={staticStyles.infoText}>
                            인증된 번호는 회원 아이디로 사용됩니다.
                        </p>
                    }

                    {isCodeSent &&
                        <p
                            className="resend-button"
                            // 로딩 중일 때 비활성화 스타일 적용
                            style={{
                                ...staticStyles.resendButton,
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                color: isLoading ? '#555' : '#9A9A9A' // 로딩 중 색상 변경
                            }}
                            onClick={handleResend}
                        >
                            인증번호 재요청
                        </p>
                    }
                </div>
            </main>

            {/* 고정 하단 버튼 */}
            <div className="bottom-button-wrapper" style={staticStyles.bottomButtonWrapper}>
                <button
                    // 로딩 상태에 따라 스타일 동적 적용
                    style={{
                        ...staticStyles.verificationPageButton,
                        // 활성화 O, 로딩 X 일때만 활성 스타일
                        ...((isButtonActive && !isLoading) ? staticStyles.verificationPageButtonActive : {}),
                        // 로딩 중일 때 로딩 스타일
                        ...(isLoading ? staticStyles.verificationPageButtonLoading : {})
                    }}
                    onClick={handleBottomButtonClick}
                    // 로딩 중일 때 비활성화
                    disabled={!isButtonActive || isLoading}
                >
                    <span className="verificationButton-label" style={staticStyles.verificationButtonLabel}>
                        {/* 로딩 상태에 따라 텍스트 변경 */}
                        {isLoading
                            ? (isCodeSent ? '확인 중...' : '전송 중...')
                            : (isCodeSent ? '확인' : '인증번호 요청')
                        }
                    </span>
                </button>
            </div>

            {/* 성공 토스트 메시지 */}
            {showSentMessage &&
                <div className="sent-message-toast" style={staticStyles.sentMessageToast}>
                    인증번호가 발송되었습니다.
                </div>
            }

            {/* 알림 모달 (에러 표시용) */}
            <ConfirmModal
                isOpen={alertModal.isOpen}
                onClose={closeAlertModal}
                onConfirm={() => {
                    closeAlertModal();
                    if (alertModal.onConfirm) alertModal.onConfirm();
                }}
                title={alertModal.title}
                message={alertModal.message}
                showCancel={false} // 알림용이므로 확인 버튼만 표시
            />
        </div>
    );
}


// Agreement.jsx의 레이아웃 스타일 + Verification.jsx의 콘텐츠 스타일
/** @type {React.CSSProperties} */
const staticStyles = {
    // --- 1. 레이아웃 (Agreement.jsx와 동일) ---
    backgroundWrapper: {
        background: '#0D042D',
        width: '100%',
        height: '100dvh', // 화면 전체 높이
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden', // 최상위 컨테이너는 스크롤되지 않음
    },
    fixedHeaderWrapper: {
        position: 'fixed',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '430px',
        zIndex: 100,
        backgroundColor: '#0D042D',
    },
    statusBar: {
        flexShrink: 0,
        backgroundColor: 'transparent',
    },
    // --- 2. 스크롤되는 메인 콘텐츠 ---
    mainContent: {
        flex: 1, // 남은 영역 모두 차지
        overflowY: 'auto', // 이 영역만 스크롤됨
        width: '100%',
        maxWidth: '430px',
        margin: '0 auto',
        // 상단 패딩은 동적으로 계산되므로 0 또는 초기값으로 설정
        paddingTop: '0',
        // 하단 버튼 영역만큼 하단 패딩 (버튼 래퍼 94px)
        paddingBottom: 'calc(env(safe-area-inset-bottom, 24px) + 94px)',
        paddingLeft: '24px',
        paddingRight: '24px',
        boxSizing: 'border-box',
    },
    // --- 3. 페이지 제목 (Verification.css 기반) ---
    verificationInfoField: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginBottom: 'clamp(40px, 10.67vw, 46px)',
        // Agreement.jsx와 동일하게 25px 간격 적용
        marginTop: '25px',
    },
    verificationInfoTitle: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '700',
        fontSize: 'clamp(24px, 6.4vw, 28px)',
        color: '#F5F8F9',
        lineHeight: 'clamp(34px, 9.07vw, 39px)',
        margin: '0 0 clamp(12px, 3.2vw, 14px) 0',
    },
    verificationInfoSubtitle: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        fontSize: 'clamp(16px, 4.27vw, 18px)',
        color: '#9A9A9A',
        lineHeight: 'clamp(24px, 6.4vw, 28px)',
        margin: 0,
    },
    // --- 4. 인증 폼 (Verification.css 기반) ---
    verificationForm: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        // marginTop: 'clamp(32px, 8.53vw, 37px)',
    },
    telnumLabel: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        fontSize: 'clamp(14px, 3.73vw, 16px)',
        color: '#9A9A9A',
        marginBottom: 'clamp(8px, 2.13vw, 9px)',
    },
    inputContainer: {
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        marginBottom: 'clamp(8px, 2.13vw, 9px)',
    },
    commonInput: {
        height: 'clamp(50px, 13.33vw, 58px)',
        padding: 'clamp(16px, 4.27vw, 18px) clamp(14px, 3.73vw, 16px)',
        fontSize: 'clamp(16px, 4.27vw, 18px)',
        // 이 borderRadius가 적용되도록 수정합니다.
        borderRadius: 'clamp(6px, 1.6vw, 7px)',
        // 'transparent' 테두리가 핵심입니다.
        border: '1px solid transparent',
        width: '100%',
        boxSizing: 'border-box',
        background: '#1B1931',

        color: '#FCFCFA',

        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',

        outline: 'none',

        '::placeholder': {
            fontFamily: '"Pretendard", sans-serif',
            fontWeight: '500',
            fontSize: 'clamp(16px, 4.27vw, 18px)',
            color: '#747474',
        },
    },

    // 'inputFocused' 스타일을 아래 코드로 교체하세요.
    inputFocused: {
        // 첫 번째 배경: 안쪽을 채울 단색 (commonInput의 배경색과 동일)
        // 두 번째 배경: 테두리가 될 그라데이션
        background: 'linear-gradient(#1B1931, #1B1931), linear-gradient(90deg, #8052FF 0%, #FFFFFF 50%, #0231FB 100%)',

        // 배경의 기준점을 '테두리(border-box)'로 설정
        backgroundOrigin: 'border-box',

        // 배경을 자르는 방식 설정
        // - 첫 번째 배경(단색)은 padding 안쪽(padding-box)에만 표시
        // - 두 번째 배경(그라디언트)은 테두리 영역(border-box)까지 표시
        backgroundClip: 'padding-box, border-box',

        // (중요) commonInput의 'transparent' 테두리를 유지해야 이 트릭이 작동합니다.
        //    기존 border-image 관련 속성은 모두 제거합니다.
    },
    clearButton: {
        position: 'absolute',
        right: 'clamp(14px, 3.73vw, 16px)',
        width: 'clamp(20px, 5.33vw, 23px)',
        height: 'clamp(20px, 5.33vw, 23px)',
        cursor: 'pointer',
    },
    timer: {
        position: 'absolute',
        right: 'clamp(14px, 3.73vw, 16px)',
        top: '50%',
        transform: 'translateY(-50%)',
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        fontSize: 'clamp(14px, 3.73vw, 16px)',
        color: '#FFFFFF',
    },
    infoText: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        fontSize: 'clamp(12px, 3.2vw, 14px)',
        color: '#9A9A9A',
        margin: 'clamp(4px, 1.07vw, 5px) 0 0 0',
    },
    resendButton: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        fontSize: 'clamp(12px, 3.2vw, 14px)',
        textDecoration: 'underline',
        color: '#9A9A9A',
        margin: 'clamp(12px, 3.2vw, 14px) 0 0 0',
        width: '100%',
        textAlign: 'right',
    },
    // --- 5. 하단 고정 버튼 (Agreement.jsx와 동일) ---
    bottomButtonWrapper: {
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '430px',
        padding: '16px 24px calc(env(safe-area-inset-bottom, 24px) + 16px)',
        boxSizing: 'border-box',
        zIndex: 100,
        backgroundColor: '#0D042D',
    },
    verificationPageButton: {
        width: '100%',
        height: 'clamp(56px, 14.93vw, 64px)',
        border: 'none',
        borderRadius: 'clamp(6px, 1.6vw, 7px)',
        background: '#3A394A',
        cursor: 'not-allowed',
        margin: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    verificationPageButtonActive: {
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        cursor: 'pointer',
    },
    // 로딩 중 버튼 스타일
    verificationPageButtonLoading: {
        background: '#5a5a6a', // 로딩 중 회색 배경
        cursor: 'wait', // 로딩 중 커서
    },
    verificationButtonLabel: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '700',
        fontSize: 'clamp(16px, 4.27vw, 18px)',
        color: '#FFFFFF',
        whiteSpace: 'nowrap',
    },
    // --- 6. 토스트 메시지 (Verification.css 기반) ---
    sentMessageToast: {
        position: 'fixed',
        bottom: 'clamp(110px, 29.3vw, 118px)', // (하단 버튼 + 여백)
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(58, 57, 74, 0.9)',
        padding: 'clamp(12px, 3.2vw, 14px) clamp(20px, 5.33vw, 23px)',
        borderRadius: 'clamp(16px, 4.27vw, 18px)',
        color: '#F5F8F9',
        fontSize: 'clamp(14px, 3.73vw, 16px)',
        fontWeight: '500',
        zIndex: 200,
        pointerEvents: 'none',
        // 텍스트가 줄바꿈되지 않도록 설정
        whiteSpace: 'nowrap',
    },
    // 에러 토스트 스타일
    errorMessageToast: {
        backgroundColor: 'rgba(255, 71, 71, 0.9)', // 에러용 붉은 배경
        color: '#FFFFFF',
    }
};

export default Verification;